import { ConsensusMode } from '../../types';
import { CONSENSUS_CONTROL_LABEL } from './ConsensusModeToggle';

export const CURRENT_POLICY_ONLY_LABEL = 'Current policy only';
export const AI_JUDGEMENTS_LABEL = 'AI Judgements';
export const CONSENSUS_LABEL = 'Consensus';
export const PRODUCTION_SOURCE_LABEL = 'Production';
export const EVAL_RUN_SOURCE_LABEL = 'Eval Run';

export type ReviewEmptyScope = 'criterion' | 'userCriterion';

export interface DisagreementCandidatesEmptyInput {
  currentPolicyOnly: boolean;
  consensusMode: ConsensusMode;
  includeAny: boolean;
  sourceKind: 'production' | 'eval';
  evalRunCoversCriterion: boolean;
  comparedCount: number;
  scope: ReviewEmptyScope;
}

export interface ReviewEmptyStateCopy {
  title: string;
  hint: string | null;
}

const nextConsensusMode = (mode: ConsensusMode, includeAny: boolean): ConsensusMode | null => {
  if (mode === 'strict') return 'lenient';
  if (mode === 'lenient') return 'inclusive';
  if (mode === 'inclusive' && includeAny) return 'any';
  return null;
};

const scopePhrase = (scope: ReviewEmptyScope): string =>
  scope === 'userCriterion' ? 'this user and criterion' : 'this criterion';

const comparisonNoun = (scope: ReviewEmptyScope): string =>
  scope === 'userCriterion' ? 'ground truth' : 'human labels';

const joinWithAnd = (parts: string[]): string => {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts.slice(-1)[0]}`;
};

const filterHints = (input: DisagreementCandidatesEmptyInput): string[] => {
  const hints: string[] = [];
  if (input.currentPolicyOnly) {
    hints.push(`Turn off ${CURRENT_POLICY_ONLY_LABEL} to include labels from older policies.`);
  }
  const nextConsensus = nextConsensusMode(input.consensusMode, input.includeAny);
  if (nextConsensus) {
    hints.push(
      `Switch ${CONSENSUS_LABEL} from ${CONSENSUS_CONTROL_LABEL[input.consensusMode]} to ${CONSENSUS_CONTROL_LABEL[nextConsensus]}.`
    );
  }
  return hints;
};

const activeFilterLabels = (input: DisagreementCandidatesEmptyInput): string[] => {
  const activeFilters: string[] = [];
  if (input.currentPolicyOnly) {
    activeFilters.push(`${CURRENT_POLICY_ONLY_LABEL} on`);
  }
  if (nextConsensusMode(input.consensusMode, input.includeAny)) {
    activeFilters.push(`${CONSENSUS_CONTROL_LABEL[input.consensusMode]} consensus`);
  }
  return activeFilters;
};

export const disagreementCandidatesEmptyCopy = (
  input: DisagreementCandidatesEmptyInput
): ReviewEmptyStateCopy => {
  const scope = scopePhrase(input.scope);
  const noun = comparisonNoun(input.scope);

  if (input.sourceKind === 'eval' && !input.evalRunCoversCriterion) {
    return {
      title: `No ${EVAL_RUN_SOURCE_LABEL} covers ${scope}.`,
      hint: `Switch ${AI_JUDGEMENTS_LABEL} to ${PRODUCTION_SOURCE_LABEL}, or select a criterion that has an ${EVAL_RUN_SOURCE_LABEL}.`,
    };
  }

  if (input.comparedCount > 0) {
    if (input.scope === 'userCriterion') {
      return {
        title: "This user's labels were compared with ground truth. There are no disagreements.",
        hint: null,
      };
    }
    return {
      title: 'AI Judgements and human labels were compared. There are no disagreements.',
      hint: null,
    };
  }

  const hints = filterHints(input);
  const filters = activeFilterLabels(input);
  if (filters.length > 0) {
    const filterPhrase = joinWithAnd(filters);
    const title =
      input.sourceKind === 'eval'
        ? `This ${EVAL_RUN_SOURCE_LABEL} has AI Judgements, but no eligible ${noun} to compare with ${filterPhrase}.`
        : `No eligible ${noun} to compare with ${filterPhrase} for ${scope}.`;
    return { title, hint: hints.join(' ') };
  }

  if (input.sourceKind === 'eval') {
    return {
      title: `This ${EVAL_RUN_SOURCE_LABEL} has AI Judgements, but no eligible ${noun} to compare for ${scope}.`,
      hint: null,
    };
  }
  return {
    title: `No disagreement candidates for ${scope}.`,
    hint: null,
  };
};

export const disagreementLocationEmptyCopy = (
  kind: 'unknownCriterion' | 'unknownTranscription'
): ReviewEmptyStateCopy => {
  if (kind === 'unknownCriterion') {
    return {
      title: 'This criterion is no longer available.',
      hint: 'It may have been removed. Choose another criterion to continue.',
    };
  }
  return {
    title: 'This transcription is no longer a disagreement candidate for this criterion.',
    hint: 'It may have been resolved or no longer matches the current filters. Choose another transcription to continue.',
  };
};

export const outputReviewEmptyCopy = (input: {
  sourceKind: 'production' | 'eval';
  evalRunCoversCriterion: boolean;
}): ReviewEmptyStateCopy => {
  if (input.sourceKind === 'eval' && !input.evalRunCoversCriterion) {
    return {
      title: `No ${EVAL_RUN_SOURCE_LABEL} covers this criterion.`,
      hint: `Switch ${AI_JUDGEMENTS_LABEL} to ${PRODUCTION_SOURCE_LABEL}, or select a criterion that has an ${EVAL_RUN_SOURCE_LABEL}.`,
    };
  }
  if (input.sourceKind === 'eval') {
    return {
      title: `No AI Judgements in this ${EVAL_RUN_SOURCE_LABEL} for this criterion.`,
      hint: `Switch ${AI_JUDGEMENTS_LABEL} to ${PRODUCTION_SOURCE_LABEL} to see Final Judgements.`,
    };
  }
  return {
    title: 'No transcriptions found for this criterion.',
    hint: null,
  };
};
